import { Trans } from '@lingui/macro'
import { Box, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetExpertiseMeetingQuery } from '@/core/services/expertise-meeting'
import { useGetExpertiseQuery } from '@/core/services/expertise'
import { Expertise as ExpertiseType } from '@/core/models/expertise.model'
import TextInputController from '@/components/ui/inputs/TextInputController'
import Documents from '@/pages/expertise/components/Documents'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useExpertiseDocuments } from '../model/useExpertiseDocuments'
import { useExpertiseForm, useExpertiseFormContext } from '../model/useExpertiseForm'
import { FormProvider } from 'react-hook-form'

type ExpertiseMeetingDocumentsProps = {
  expertiseMeetingId: string
}

export function Expertise({ expertiseMeetingId }: ExpertiseMeetingDocumentsProps) {
  const authUser = useAppSelector(selectAuthUser)
  const { data: expertiseMeeting } = useGetExpertiseMeetingQuery(expertiseMeetingId)
  const { data: expertise } = useGetExpertiseQuery(expertiseMeeting?.expertise ?? skipToken)

  if (!expertise) return null

  const canEdit = authUser?._id === expertise.createdBy._id
  return <ExpertiseForm canEdit={canEdit} expertise={expertise} />
}

function ExpertiseForm({ canEdit = false, expertise }: { canEdit?: boolean; expertise: ExpertiseType }) {
  const { isSubmitting, onSubmit, rhForm } = useExpertiseForm(expertise)
  return (
    <FormProvider {...rhForm}>
      <form onSubmit={rhForm.handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Conclusion canEdit={canEdit} isSubmitting={isSubmitting} />
          <Diagnostic canEdit={canEdit} />
          <ExpertiseDocuments expertiseId={expertise._id} />
        </Stack>
      </form>
    </FormProvider>
  )
}

function Conclusion({ canEdit, isSubmitting }: { canEdit?: boolean; isSubmitting: boolean }) {
  const {
    control,
    formState: { dirtyFields, isValid },
  } = useExpertiseFormContext()
  return (
    <Stack direction="column" justifyContent="flex-end" alignItems="start" spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
          <Trans>Conclusion</Trans>
        </Typography>
        {dirtyFields.conclusion && (
          <LoadingButton disabled={!isValid} loading={isSubmitting} size="small" variant="outlined" type="submit">
            <Trans>Save</Trans>
          </LoadingButton>
        )}
      </Stack>
      <Stack direction="row" spacing={2} width="100%">
        <Box flex={1}>
          <TextInputController
            control={control}
            fullWidth
            inputProps={{ 'data-testid': 'conclusion-textarea' }}
            maxRows={6}
            minRows={4}
            multiline
            name="conclusion"
            readOnly={!canEdit}
          />
        </Box>
      </Stack>
    </Stack>
  )
}

function Diagnostic({ canEdit }: { canEdit?: boolean }) {
  const { control } = useExpertiseFormContext()
  return (
    <Stack direction="column" justifyContent="flex-end" alignItems="start" spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
          <Trans>Diagnostic</Trans>
        </Typography>
      </Stack>
      <TextInputController
        control={control}
        fullWidth
        inputProps={{ 'data-testid': 'diagnostic-textarea' }}
        maxRows={6}
        minRows={4}
        multiline
        name="diagnostic"
        readOnly={!canEdit}
      />
    </Stack>
  )
}

function ExpertiseDocuments({ expertiseId }: { expertiseId: string }) {
  const { canUpload, documents, deleteDocuments, isDeletingFiles, uploadDocument } = useExpertiseDocuments(expertiseId)

  return <Documents isDeleting={isDeletingFiles} documents={documents} onDelete={deleteDocuments} onUpload={uploadDocument} readOnly={!canUpload} />
}
